import React from "react";
import { TextStyle, View } from "react-native";
import SkeletonLoader from "./SkeletonLoader";
import AppText from "./AppText";
import OverflowTooltipText from "./OverflowTooltipText";

interface FormattedTextProps {
  testID?: string;
  value: number | null | undefined;
  type?: "currency" | "percentage" | "number";
  decimalPlaces?: number;
  style?: TextStyle;
  loading?: boolean;
  rowWidth?: string | number;
  tooltip?: boolean;
  tooltipValue?: string;
  containerWidth?: string | number;
  onPress?: () => void;
}

export const formatValue = (
  value: number | null | undefined,
  type: string = "currency",
  decimalPlaces: number = 2
): string => {
  if (value === null || value === undefined) {
    if (type === "currency") {
      return `$${(0).toFixed(decimalPlaces)}`;
    }
    if (type === "percentage") {
      return `0.00%`;
    }
    if (type === "number") {
      return (0).toFixed(decimalPlaces);
    }
  }

  if (typeof value === "number") {
    if (type === "currency") {
      const absValue = Math.abs(value).toLocaleString(undefined, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });
      return value < 0 ? `($${absValue})` : `$${absValue}`;
    }

    if (type === "percentage") {
      return `${value.toFixed(decimalPlaces)}%`;
    }

    if (type === "number") {
      return value.toLocaleString(undefined, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });
    }
  }

  return "Invalid Value";
};

const FormattedText: React.FC<FormattedTextProps> = ({
  testID,
  value,
  type = "currency",
  decimalPlaces = 2,
  style,
  loading = false,
  rowWidth = "60%",
  tooltip = false,
  tooltipValue,
  containerWidth,
  onPress,
}) => {
  if (loading) {
    return (
      <View>
        <SkeletonLoader rows={1} rowWidth="60%" />
      </View>
    );
  }

  const formattedValue = formatValue(value, type, decimalPlaces);

  if (tooltip) {
    return (
      <OverflowTooltipText
        testID={testID}
        text={formattedValue}
        tooltipValue={tooltipValue ?? formattedValue}
        style={style}
        containerWidth={containerWidth}
        onPress={onPress}
      />
    );
  }

  return (
    <AppText style={style} testID={testID}>
      {formattedValue}
    </AppText>
  );
};

export default FormattedText;
